import { FunctionComponent, useState } from "react";
import { InscriptionLight } from "../../services/inscription";
import { useLocation } from "react-router-dom";
import { Spin, Modal, Typography } from "antd";
import { ExclamationCircleFilled, TeamOutlined, UserOutlined } from "@ant-design/icons";
import { useMediaQuery } from 'react-responsive';
import { ModaleConfirmSuppressionInscription } from "../../components/modals/ModalConfirmSuppressionInscription";
import exportToExcel, { ExcelColumnHeadersType } from "../../utils/FormUtils";
import { useAuth } from "../../hooks/AuthContext";
import { useInscriptionManagement } from "./cours/hooks/useInscriptionManagement";
import { InscriptionMobileView } from "./cours/InscriptionMobileView";
import { InscriptionDesktopView } from "./cours/InscriptionDesktopView";
import { UnahtorizedAccess } from "../public/UnahtorizedAccess";
import { BatchLockAlert } from "../../components/common/BatchLockAlert";

export const AdminCoursArabes: FunctionComponent = () => {
    const location = useLocation();
    const application = location.state?.application;
    const type = application === "COURS_ADULTE" ? "ADULTE" : "ENFANT";
    const { roles } = useAuth();
    const isMobile = useMediaQuery({ maxWidth: 768 });
    const [modaleConfirmSuppressionOpen, setModaleConfirmSuppressionOpen] = useState<boolean>(false);
    const icon = type === "ENFANT" ? <TeamOutlined /> : <UserOutlined />;

    const {
        dataSource,
        setDataSource,
        selectedInscriptions,
        setSelectedInscriptions,
        periodesOptions,
        isLoading,
        searchInscriptions,
        validateInscription,
        validateInscriptions,
        deleteInscription,
        deleteInscriptions,
        getSelectedInscriptionDistinctIds,
        batchLockConflict,
        setBatchLockConflict,
    } = useInscriptionManagement({ application, type });

    let excelColumnHeaders: ExcelColumnHeadersType<InscriptionLight> = {
        nom: "Nom élève",
        prenom: "Prénom élève",
        dateNaissance: "Date naissance",
        niveauInterne: "Niveau interne",
        mobile: "Tél.",
        email: "E-mail",
        ville: "Ville",
        noInscription: "Numéro inscription",
        dateInscription: "Date d'inscription",
        montantTotal: "Montant dû",
        montantRegle: "Montant réglé",
    };

    if (application === "COURS_ENFANT") {
        excelColumnHeaders = {
            ...excelColumnHeaders,
            niveau: "Niveau publique",
            nomResponsableLegal: "Nom responsable légal",
            prenomResponsableLegal: "Prénom responsable légal",
            nomContactUrgence: "Nom autre contact",
            prenomContactUrgence: "Prénom autre contact",
            mobileContactUrgence: "Tél. autre contact",
            autorisationAutonomie: "Autorisation à rentrer seul",
            autorisationMedia: "Autorisation photos/vidéos",
        }
    }

    const exportData = () => {
        if (dataSource) {
            exportToExcel<InscriptionLight>(dataSource, excelColumnHeaders, `inscriptions-${type}`);
        }
    };

    const handleDeleteInscription = async (inscriptionId: number) => {
        Modal.confirm({
            title: "Attention",
            icon: <ExclamationCircleFilled />,
            content: (
                <div>
                    <p>Vous êtes sur le point de supprimer cette inscription.</p>
                    <p>Cette action entraînera la <strong>suppression définitive</strong> de :</p>
                    <ul>
                        <li>Toutes les données liées à cette inscription</li>
                        <li>Les élèves rattachés à cette inscription</li>
                        <li>Les bulletins associés</li>
                    </ul>
                    <p><strong>Cette action est irréversible.</strong></p>
                </div>
            ),
            okText: "Supprimer",
            okType: "danger",
            cancelText: "Annuler",
            onOk: () => deleteInscription(inscriptionId),
            okButtonProps: { testid: "confirmer-suppression" } as any,
            cancelButtonProps: { testid: "annuler-suppression" } as any,
        });
    };

    const handleDeleteInscriptions = async () => {
        setModaleConfirmSuppressionOpen(false);
        await deleteInscriptions(selectedInscriptions);
    }

    return roles?.includes("ROLE_ADMIN") ? (
        <div className="centered-content">
            <div className="container-full-width">
                <Spin spinning={isLoading}>
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                        paddingBottom: "16px",
                        borderBottom: `3px solid ${type === "ENFANT" ? "#4FC3F7" : "#1A73E8"}`,
                        marginBottom: "24px",
                        marginTop: "16px",
                    }}>
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "48px",
                            height: "48px",
                            borderRadius: "10px",
                            backgroundColor: type === "ENFANT" ? "#4FC3F7" : "#1A73E8",
                            flexShrink: 0,
                        }}>
                            <span style={{ fontSize: "24px", color: "#ffffff" }}>{icon}</span>
                        </div>
                        <div>
                            <Typography.Title level={3} style={{ margin: 0, lineHeight: 1.2 }}>
                                Inscriptions {type === "ENFANT" ? "cours enfants" : "cours adultes"}
                            </Typography.Title>
                            <Typography.Text type="secondary">
                                Gestion et suivi des inscriptions
                            </Typography.Text>
                        </div>
                    </div>

                    <BatchLockAlert
                        show={batchLockConflict.show}
                        resourceType={batchLockConflict.resourceType}
                        username={batchLockConflict.username}
                        expiresAt={batchLockConflict.expiresAt}
                    />

                    <div className="search-result-container">
                        {isMobile ? (
                            <InscriptionMobileView
                                application={application}
                                type={type}
                                dataSource={dataSource}
                                setDataSource={setDataSource}
                                selectedInscriptions={selectedInscriptions}
                                setSelectedInscriptions={setSelectedInscriptions}
                                periodesOptions={periodesOptions}
                                onValidateInscription={validateInscription}
                                onValidateInscriptions={validateInscriptions}
                                onDeleteInscription={handleDeleteInscription}
                                onDeleteInscriptions={async (inscriptions) => { setModaleConfirmSuppressionOpen(true); }}
                                onSearch={searchInscriptions}
                                onExport={exportData}
                            />
                        ) : (
                            <InscriptionDesktopView
                                application={application}
                                type={type}
                                dataSource={dataSource}
                                setDataSource={setDataSource}
                                selectedInscriptions={selectedInscriptions}
                                setSelectedInscriptions={setSelectedInscriptions}
                                periodesOptions={periodesOptions}
                                onValidateInscription={validateInscription}
                                onValidateInscriptions={validateInscriptions}
                                onDeleteInscription={handleDeleteInscription}
                                onDeleteInscriptions={async (inscriptions) => { setModaleConfirmSuppressionOpen(true); }}
                                onSearch={searchInscriptions}
                                onExport={exportData}
                            />
                        )}
                    </div>

                    <ModaleConfirmSuppressionInscription
                        open={modaleConfirmSuppressionOpen}
                        setOpen={setModaleConfirmSuppressionOpen}
                        nbInscriptions={getSelectedInscriptionDistinctIds().length}
                        onConfirm={handleDeleteInscriptions}
                    />
                </Spin>
            </div>
        </div>
    ) : <UnahtorizedAccess />
};